import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  TextField,
  Typography,
  Stack,
  Link,
  Alert,
  CircularProgress,
  Divider,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth';
import { loginSchema, LoginFormData } from '../validations/auth';

export default function SignInPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { login: authLogin, isAuthenticated, isProfileLoaded } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  // const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [waitingForAuth, setWaitingForAuth] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const from = location.state?.from?.pathname || '/';

  // Watch for authentication state changes and navigate when ready
  useEffect(() => {
    if (waitingForAuth && isAuthenticated && isProfileLoaded) {
      console.log('Auth state ready, navigating to:', from);
      setWaitingForAuth(false);
      navigate(from, { replace: true });
    }
  }, [waitingForAuth, isAuthenticated, isProfileLoaded, from, navigate]);

  // Add timeout for authentication waiting
  useEffect(() => {
    if (waitingForAuth) {
      const timeout = setTimeout(() => {
        setWaitingForAuth(false);
        navigate(from, { replace: true });
      }, 5000); // 5 second timeout

      return () => clearTimeout(timeout);
    }
  }, [waitingForAuth, from, navigate]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<LoginFormData>({
    resolver: yupResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
      saveId: false,
      keepSignedIn: false,
    },
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      setIsLoading(true);
      
      // Use Firebase authentication directly
      await authLogin({
        email: data.email,
        password: data.password,
      });
      
      console.log('Login successful, setting waiting state...'); // Debug log
      
      // Set waiting state - the useEffect will handle navigation when auth is ready
      setWaitingForAuth(true);
      
    } catch (error: unknown) {
      console.error('Login error:', error);
      
      // Handle Firebase authentication errors
      if (error && typeof error === 'object' && 'code' in error) {
        const firebaseError = error as { code: string; message?: string };
        switch (firebaseError.code) {
          case 'auth/user-not-found':
          case 'auth/wrong-password':
          case 'auth/invalid-credential':
            setError('root', {
              type: 'manual',
              message: t('auth.errors.invalidCredentials'),
            });
            break;
          case 'auth/invalid-email':
            setError('root', {
              type: 'manual',
              message: t('auth.errors.invalidEmail'),
            });
            break;
          case 'auth/too-many-requests':
            setError('root', {
              type: 'manual',
              message: t('auth.errors.tooManyRequests'),
            });
            break;
          case 'auth/user-disabled':
            setError('root', {
              type: 'manual',
              message: t('auth.errors.userDisabled'),
            });
            break;
          default:
            setError('root', {
              type: 'manual',
              message: firebaseError.message || t('auth.errors.loginFailed'),
            });
        }
      } else {
        setError('root', {
          type: 'manual',
          message: t('auth.errors.loginFailed'),
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // const handleGoogleSignIn = async () => {
  //   // Prevent multiple concurrent requests
  //   if (isGoogleLoading || isLoading) {
  //     return;
  //   }
  //
  //   try {
  //     setIsGoogleLoading(true);
  //     await loginWithGoogle();
  //     console.log('Google login successful, setting waiting state...');
  //     // Set waiting state - the useEffect will handle navigation when auth is ready
  //     setWaitingForAuth(true);
  //   } catch (error: unknown) {
  //     console.error('Google sign-in error:', error);
  //
  //     // Handle Google sign-in errors
  //     if (error && typeof error === 'object' && 'code' in error) {
  //       const firebaseError = error as { code: string; message?: string };
  //       switch (firebaseError.code) {
  //         case 'auth/popup-closed-by-user':
  //           setError('root', {
  //             type: 'manual',
  //             message: 'Sign-in was cancelled. Please try again.',
  //           });
  //           break;
  //         case 'auth/popup-blocked':
  //           setError('root', {
  //             type: 'manual',
  //             message: 'Pop-up was blocked. Please allow pop-ups and try again.',
  //           });
  //           break;
  //         case 'auth/cancelled-popup-request':
  //           setError('root', {
  //             type: 'manual',
  //             message: 'Please wait for the current sign-in to complete.',
  //           });
  //           break;
  //         default:
  //           setError('root', {
  //             type: 'manual',
  //             message: firebaseError.message || 'Google sign-in failed. Please try again.',
  //           });
  //       }
  //     } else {
  //       setError('root', {
  //         type: 'manual',
  //         message: 'Google sign-in failed. Please try again.',
  //       });
  //     }
  //   } finally {
  //     setIsGoogleLoading(false);
  //   }
  // };

  return (
    <Container
      maxWidth="xs"
      sx={{
        py: 8,
        minHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ width: '100%' }}>
        <Typography variant="h5" fontWeight={700} align="center" gutterBottom>
          {t('auth.signIn')}
        </Typography>
        <Typography variant="body2" align="center" sx={{ mb: 0.5 }}>
          {t('brand.enjoyExclusiveServices')}
        </Typography>
        <Typography
          variant="subtitle2"
          align="center"
          sx={{ mb: 3, fontWeight: 700 }}
        >
          {t('brand.fromHatsuhiken')}
        </Typography>
        
        {errors.root && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.root.message}
          </Alert>
        )}
        
        <Stack spacing={2}>
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t('auth.email')}
                variant="outlined"
                fullWidth
                size="small"
                type="email"
                error={!!errors.email}
                helperText={errors.email?.message}
                disabled={isLoading}
              />
            )}
          />
          
          <Controller
            name="password"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t('auth.password')}
                variant="outlined"
                fullWidth
                size="small"
                type={showPassword ? 'text' : 'password'}
                error={!!errors.password}
                helperText={errors.password?.message}
                disabled={isLoading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        disabled={isLoading}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Controller
              name="saveId"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox 
                      {...field}
                      checked={field.value}
                      size="small" 
                      disabled={isLoading}
                    />
                  }
                  label={
                    <Typography variant="body2">{t('auth.saveId')}</Typography>
                  }
                  sx={{ mr: 2 }}
                />
              )}
            />
            <Controller
              name="keepSignedIn"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox 
                      {...field}
                      checked={field.value}
                      size="small" 
                      disabled={isLoading}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {t('auth.keepSignedIn')}
                    </Typography>
                  }
                />
              )}
            />
          </Box>
          
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={isLoading || waitingForAuth}
            sx={{
              bgcolor: '#b8957b',
              color: 'white',
              fontWeight: 700,
              '&:hover': { bgcolor: '#a98c74' },
              '&:disabled': { bgcolor: '#ccc' },
            }}
          >
            {isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : waitingForAuth ? (
              <>
                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                {t('auth.signingIn')}
              </>
            ) : (
              t('auth.signIn')
            )}
          </Button>
        </Stack>
        
        <Box sx={{ mt: 1, mb: 2 }}>
          <Link
            component="button"
            onClick={() => navigate('/forgot-password')}
            underline="none"
            sx={{ fontSize: 14, color: 'text.secondary', cursor: 'pointer' }}
          >
            {t('auth.forgotPassword')}
          </Link>
        </Box>
        
        <Divider sx={{ my: 3 }}>{t('auth.or')}</Divider>
        
        <Stack spacing={1}>
          {/* <Button
            variant="outlined"
            fullWidth
            startIcon={
              <img src="/icons/line.svg" alt="LINE" width={24} height={24} />
            }
            sx={{
              borderColor: '#b8957b',
              color: '#b8957b',
              textTransform: 'none',
              fontWeight: 500,
            }}
            disabled={isLoading}
          >
            {t('auth.signInWith')} {t('social.line')}
          </Button> */}
          {/* <Button
            variant="outlined"
            fullWidth
            startIcon={
              isGoogleLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <img
                  src="/icons/google.svg"
                  alt="Google"
                  width={24}
                  height={24}
                />
              )
            }
            onClick={handleGoogleSignIn}
            sx={{
              borderColor: '#b8957b',
              color: '#b8957b',
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': {
                borderColor: '#a98c74',
                backgroundColor: 'rgba(184, 149, 123, 0.04)',
              },
              '&:disabled': { 
                borderColor: '#ccc',
                color: '#ccc'
              },
            }}
            disabled={isLoading || isGoogleLoading || waitingForAuth}
          >
            {isGoogleLoading 
              ? 'Signing in...' 
              : waitingForAuth
              ? 'Preparing...'
              : `${t('auth.signInWith')} ${t('social.google')}`
            }
          </Button> */}
          {/* <Button
            variant="outlined"
            fullWidth
            startIcon={
              <img src="/icons/apple.svg" alt="Apple" width={24} height={24} />
            }
            sx={{
              borderColor: '#b8957b',
              color: '#b8957b',
              textTransform: 'none',
              fontWeight: 500,
            }}
            disabled={isLoading}
          >
            {t('auth.signInWith')} {t('social.apple')}
          </Button> */}
        </Stack>
        
        <Box sx={{ mt: 5, mb: 2, textAlign: 'center' }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
            {t('benefits.dontMissOut')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('benefits.hatsuhikenBenefits')}
          </Typography>
          <Box
            sx={{ display: 'flex', justifyContent: 'center', gap: 4, mb: 2 }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <img
                src="/icons/ic_b_day_off.svg"
                alt="B-DAY"
                width={40}
                height={40}
              />
              <Typography
                variant="caption"
                sx={{ color: '#b8957b', fontWeight: 500, mt: 1 }}
              >
                {t('benefits.bdayOff')}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <img
                src="/icons/ic_app_point.svg"
                alt="Points"
                width={40}
                height={40}
              />
              <Typography
                variant="caption"
                sx={{ color: '#b8957b', fontWeight: 500, mt: 1 }}
              >
                {t('benefits.appInstallPoints')}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
      
      <Box sx={{ mt: 4, textAlign: 'center', width: '100%' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t('auth.dontHaveAccount')}
        </Typography>
        <Button
          variant="contained"
          fullWidth
          onClick={() => navigate('/register')}
          sx={{
            bgcolor: '#b8957b',
            color: 'white',
            fontWeight: 700,
            '&:hover': { bgcolor: '#a98c74' },
            '&:disabled': { bgcolor: '#ccc' },
          }}
        >
          {t('auth.joinNow')}
        </Button>
      </Box>
    </Container>
  );
} 